import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AmplifyHttpClient } from '../auth/http.amplify-httpclient';
import { HttpClient } from '@angular/common/http';
import { loaderTypes } from '@app/shared/component/loader/loader';

@Injectable({
  providedIn: 'root',
})
export class WorkSpacesService {
  private API_WORKSPACE_URL = 'api/workspace/workspace';
  constructor(private readonly httpService: AmplifyHttpClient, private http: HttpClient) {}

  getWorkSpaces(): Observable<any> {
    return this.httpService.get<any>(`${this.API_WORKSPACE_URL}`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response?.status === 200) {
          return response?.data;
        } else {
          throw new Error('Unexpected status code: ' + response?.status);
        }
      }),
      tap(async (response) => {}),
      catchError((error) => {
        return of({ error: true, message: error.message || 'An error occurred' });
      })
    );
  }

  getWorkSpacesVaults(id): Observable<any> {
    return this.httpService.get<any>(`${this.API_WORKSPACE_URL}-vaults/${id}`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          throw new Error('Unexpected status code: ' + response?.status);
        }
      }),
      catchError((error) => {
        return of({ error: true, message: error.message || 'An error occurred' });
      })
    );
  }

  createWorkspace(request): Observable<any> {
    return this.httpService.post<any>(`${this.API_WORKSPACE_URL}`, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateWorkspace(request): Observable<any> {
    return this.httpService.patch<any>(`${this.API_WORKSPACE_URL}`, request, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteWorkspace(workspaceId): Observable<any> {
    return this.httpService.delete<any>(`${this.API_WORKSPACE_URL}/${workspaceId}`).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getWorkspaceMember(workspaceId, type): Observable<any> {
    return this.httpService.get<any>(`${this.API_WORKSPACE_URL}-member/${workspaceId}/${type}`, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  addWorkspaceMember(request): Observable<any> {
    return this.httpService.post<any>(`${this.API_WORKSPACE_URL}-member`, request).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteWorkspaceMember(workspaceMemberId): Observable<any> {
    return this.httpService.delete<any>(`${this.API_WORKSPACE_URL}-member/${workspaceMemberId}`).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getWorkSpaceInvites(): Observable<any> {
    return this.httpService.get<any>(`${this.API_WORKSPACE_URL}-invite`, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response?.status === 200) {
          return response?.data;
        } else {
          return response;
        }
      }),
      catchError((error) => {
        return of({ error: true, message: error?.message || 'An error occurred' });
      })
    );
  }
  deleteWorkSpace(id): Observable<any> {
    return this.httpService.delete<any>(`${this.API_WORKSPACE_URL}/${id}`, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response?.status === 200) {
          return response?.data;
        } else {
          return response;
        }
      }),
      catchError((error) => {
        return of({ error: true, message: error?.message || 'An error occurred' });
      })
    );
  }

  updateWorkSpaceInvites(payload: { workspace_id: string; invite_response: 'accept' | 'decline' }): Observable<any> {
    return this.httpService.patch<any>(`${this.API_WORKSPACE_URL}-invite`, payload, loaderTypes.none).pipe(
      map((response: any) => {
        if (response?.status === 200) {
          return response?.data;
        } else {
          return response;
        }
      }),
      catchError((error) => {
        return of({ error: true, message: error?.message || 'An error occurred' });
      })
    );
  }

  getWorkspaceVaultConfig(workspaceId): Observable<any> {
    return this.httpService.get<any>(`api/workspace/workspace-config-vaults/${workspaceId}`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  addWorkspaceVaultConfig(request: { workspace_id: string; vaults: Array<any> }): Observable<any> {
    return this.httpService.post<any>(`api/workspace/workspace-config-vaults`, request).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteWorkspaceVaultConfig(workspaceId, vaultId): Observable<any> {
    return this.httpService.delete<any>(`api/workspace/workspace-config-vaults/${workspaceId}/${vaultId}`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response?.status === 200) {
          return response?.data;
        } else {
          return response;
        }
      }),
      catchError((error) => {
        return of({ error: true, message: error?.message || 'An error occurred' });
      })
    );
  }

  getWorkspaceVaultConfigRegisters(workspaceId): Observable<any> {
    return this.httpService.get<any>(`api/workspace/workspace-config-registers/${workspaceId}`, null, loaderTypes.mainAnimation).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  addWorkspaceVaultConfigRegisters(request: {
    types_add: Array<'inbox' | 'contact' | 'password' | 'encryption_key' | 'data_feeds' | 'email_ids'>;
    workspace_id: string;
  }): Observable<any> {
    return this.httpService.post<any>(`api/workspace/workspace-config-registers`, request).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteWorkspaceVaultConfigRegisters(
    workspace_id: string,
    type: 'inbox' | 'contact' | 'password' | 'encryption_key' | 'data_feeds' | 'email_ids'
  ): Observable<any> {
    return this.httpService.delete<any>(`api/workspace/workspace-config-registers/${workspace_id}/${type}`).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
}
